/* eslint-disable react-hooks/exhaustive-deps */
import { Pagination, PaginationItem } from '@mui/material';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { FaPlus } from 'react-icons/fa';
import DeleteIcon from '../../../assets/icons/adminDelete.svg';
import CloseIcon from '../../../assets/icons/close.svg';
import api from '../../../services/api';
import handleError from '../../../utils/handleError';
import { ModalConfirm } from '../../../shared/ModalConfirm/ModalConfirm';
import { Paginated } from '../../../interfaces/Paginated';
import { ModalRegisterTag } from '../ModalRegisterTag/ModalRegisterTag';
import {
  Button,
  ButtonsContainer,
  Close,
  CloseButton,
  Container,
  Content,
  Icon,
  IconButton,
  Input,
  ModalHeader,
  PaginationWrapper,
  RowContainer,
  Table,
  TableWrapper,
  TagImage,
  Title,
} from './styles';

const styles = {
  background: 'transparent',
  fontSize: '1rem',
  fontWeight: 500,
  margin: 0,
  padding: '0.125rem',
  minWidth: '1rem',
};

interface ITag {
  id: string;
  name: string;
  image_url: string;
  created_at: string;
  updated_at: string;
}

interface Props {
  setOpenModal: Dispatch<SetStateAction<boolean>>;
}

export const ModalTagsList: React.FC<Props> = ({ setOpenModal }) => {
  const [deletingId, setDeletingId] = useState<string | undefined>();
  const [registerModal, setRegisterModal] = useState(false);

  const [tags, setTags] = useState<ITag[]>([]);
  const [defaults, setDefaults] = useState<ITag[]>([]);
  const [editingTag, setEditingTag] = useState<ITag | undefined>();

  const [page, setPage] = useState(1);
  const [maximumPage, setMaximumPage] = useState(1);
  const [search, setSearch] = useState('');

  const getTags = async () => {
    try {
      const params = {
        page,
        limit: 10,
      };

      const { data } = await api.get<Paginated<ITag>>(`/tags`, {
        params,
      });

      setTags(data.results);
      setDefaults(data.results);
      setMaximumPage(Math.ceil(data.total / 10));
    } catch (error: any) {
      handleError(error, 'error');
    }
  };

  useEffect(() => {
    getTags();
  }, [page]);

  const handleSearch = async () => {
    if (!search) return setTags(defaults);

    try {
      const params = {
        'filters[name]': search,
        page: 1,
        limit: 10,
      };

      const { data } = await api.get<Paginated<ITag>>(`/tags`, {
        params,
      });

      setTags(data.results);
    } catch (error: any) {
      handleError(error, 'error');
    }
  };

  const onDelete = async () => {
    if (!deletingId) return;

    try {
      await api.delete(`/tags/${deletingId}`);

      setDeletingId(undefined);
      handleError('Tag deletada com sucesso.', 'success');
      getTags();
    } catch (error: any) {
      handleError(error, 'error');
    }
  };

  const handleEdit = (tag: ITag) => {
    setEditingTag(tag);
    setRegisterModal(true);
  };

  const handleCloseRegister = () => {
    setRegisterModal(false);
    setEditingTag(undefined);
    getTags();
  };

  return (
    <Container>
      <Content>
        <ModalHeader>
          <Title>Listagem de Tags</Title>
          <CloseButton onClick={() => setOpenModal(false)}>
            <Close src={CloseIcon} />
          </CloseButton>
        </ModalHeader>

        <RowContainer>
          <Input
            placeholder="Buscar por nome"
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearch(e.target.value)
            }
            onBlur={() => handleSearch()}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
              e.key === 'Enter' && handleSearch()
            }
          />

          <Button type="button" onClick={() => setRegisterModal(true)}>
            <FaPlus size={18} />
            Adicionar
          </Button>
        </RowContainer>

        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <th>Imagem</th>
                <th>Nome</th>
                <th style={{ textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>

            <tbody>
              {tags.map(tag => (
                <tr key={tag.id}>
                  <td style={{ width: '250px' }}>
                    <TagImage src={tag.image_url} alt={tag.name} />
                  </td>

                  <td>{tag.name}</td>

                  <td
                    style={{
                      maxWidth: '350px',
                      minWidth: '350px',
                      textAlign: 'right',
                    }}
                  >
                    <ButtonsContainer>
                      <IconButton type="button" onClick={() => handleEdit(tag)}>
                        Editar
                      </IconButton>
                      <IconButton
                        type="button"
                        onClick={() => setDeletingId(tag.id)}
                      >
                        <Icon src={DeleteIcon} />
                      </IconButton>
                    </ButtonsContainer>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>

        {maximumPage > 1 && (
          <PaginationWrapper>
            <Pagination
              page={page}
              count={maximumPage}
              siblingCount={1}
              onChange={(e, _page) => setPage(_page)}
              hidePrevButton={page === 1}
              hideNextButton={page === maximumPage}
              renderItem={item => {
                return (
                  <PaginationItem
                    {...item}
                    style={{
                      ...styles,
                      color: item.page === page ? '#4793CE' : '#888888',
                    }}
                    classes={{ icon: 'icon' }}
                  />
                );
              }}
            />
          </PaginationWrapper>
        )}
      </Content>

      {deletingId && (
        <ModalConfirm
          onCancel={() => setDeletingId(undefined)}
          onConfirm={onDelete}
          title="Tem certeza que deseja excluir essa tag?"
        />
      )}

      {registerModal && (
        <ModalRegisterTag
          onClose={handleCloseRegister}
          editingTag={editingTag}
        />
      )}
    </Container>
  );
};
